import {
  BarChart3,
  BookOpen,
  Download,
  LogOut,
  ShieldCheck,
  Sparkles,
  Table2,
} from "lucide-react";
import type { Route, Service } from "@/lib/utils/daylight-insight/types";
import { toDisplayLabel } from "@/lib/utils/daylight-insight/format";
import { useAuth } from "@/contexts/AuthContext";
import { useCharlieEnabled } from "@/config/featureFlags";
import { useStore } from "@/provider/store";
import { logOutUrl } from "@/service/reqUrl.service";

/** The service lines the toggle can offer, in display order. Only the ones the
 *  SP actually returned for this user are rendered. */
const SERVICE_SEG: { key: Service; full: string; abbr: string }[] = [
  { key: "hh", full: "Home Health", abbr: "HH" },
  { key: "hos", full: "Hospice", abbr: "HOS" },
];
const serviceOf = (label: string): Service =>
  label.toUpperCase().includes("HOSPICE") ? "hos" : "hh";

interface BrandRowProps {
  route: Route;
  setRoute: (r: Route) => void;
  service: Service;
  setService: (s: Service) => void;
  /** Omitted on screens that own their export (All Metrics has a per-tab Export
   *  in its own toolbar), which hides the header button rather than shipping two
   *  Export controls that write different things. */
  onExport?: () => void;
}
export function BrandRow({
  route,
  setRoute,
  service,
  setService,
  onExport,
}: BrandRowProps) {
  const { token, activeApp, user, logout } = useAuth();
  // Charlie is account-gated while it runs on mock data — the tab only exists
  // for the accounts listed in `constant.charlieAccounts`.
  const charlieEnabled = useCharlieEnabled();
  // Roles come from the SP (`UserRoles`), so the picker is fully dynamic.
  const { serviceLines, dataRefreshDate, roles, roleId, setRoleId } =
    useStore();

  // Offer only the service lines this user actually has. Before the config
  // lands `serviceLines` is empty — show both rather than an empty toggle.
  const available = serviceLines.length
    ? SERVICE_SEG.filter((seg) =>
        serviceLines.some((sl) => serviceOf(sl.label) === seg.key),
      )
    : SERVICE_SEG;

  // Derive the signed-in user's display name + initials from the live session.
  const name: string = user?.name || user?.full_name || "";
  // The tenant this session belongs to, from the login response — what the
  // avatar's tooltip says. The initials already stand for the user's name; the
  // customer is the thing nothing else on screen states. Falls back to the name
  // on a session that predates the field, so the tooltip is never empty.
  const customerName: string = user?.customerName || "";
  const avatarTitle = customerName || name;
  const initials = name
    ? name
        .split(" ")
        .map((word) => word.charAt(0))
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "";

  // Sign out server-side (invalidate this device's session), then clear the
  // local session — the route guards redirect to /login once the token clears.
  const handleLogout = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}${logOutUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Request-Type": "login",
          "app-source": activeApp,
          // Sign out only this device; leave other sessions logged in.
          deviceid: localStorage.getItem("deviceId") || "",
        },
        body: JSON.stringify({
          confirmLogout: true,
          Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const data = await res.json();
      if (data.status === 200) logout();
    } catch (error) {
      console.log(error, "error===");
    }
  };

  return (
    <div className="dl-brandrow">
      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <svg width="30" height="30" viewBox="0 0 30 30">
          <circle
            cx="15"
            cy="15"
            r="11"
            fill="none"
            stroke="var(--hermes)"
            strokeWidth="2.6"
            strokeDasharray="1.6 3.1"
            strokeLinecap="round"
          />
          <circle cx="15" cy="15" r="5.4" fill="var(--hermes)" />
        </svg>
        <div>
          <div className="dl-word">
            <span className="b">Day</span>
            <span className="a">Light</span>
          </div>
          <div className="dl-sub">by Skyra.AI</div>
        </div>
      </div>
      <div className="dl-nav">
        <button
          className={route === "operating" ? "on" : ""}
          onClick={() => setRoute("operating")}
        >
          <BarChart3 size={14} />
          <span className="nav-lbl">Operating View</span>
        </button>
        <button
          className={route === "all-metrics" ? "on" : ""}
          onClick={() => setRoute("all-metrics")}
        >
          <Table2 size={14} />
          <span className="nav-lbl">All Metrics</span>
        </button>

        <button
          className={route === "guide" ? "on" : ""}
          onClick={() => setRoute("guide")}
        >
          <BookOpen size={14} />
          <span className="nav-lbl">Guide</span>
        </button>

        {charlieEnabled && (
          <button
            className={route === "charlie" ? "on" : ""}
            onClick={() => setRoute("charlie")}
          >
            <Sparkles size={14} />
            <span className="nav-lbl">Charlie</span>
          </button>
        )}
      </div>
      <div className="dl-spacer" />
      {
        // dataRefreshDate && <span className="dl-sub" title="Last warehouse refresh">Data as of {dataRefreshDate}</span>
      }
      {available.length > 1 && (
        <div className="dl-seg">
          {available.map((seg) => (
            <button
              key={seg.key}
              className={service === seg.key ? "on" : ""}
              onClick={() => setService(seg.key)}
            >
              <span className="seg-full">{seg.full}</span>
              <span className="seg-abbr">{seg.abbr}</span>
            </button>
          ))}
        </div>
      )}
      <div className="dl-vr hidden" />
      {roles.length > 0 && (
        <div className="dl-rolepick hidden">
          <ShieldCheck size={14} style={{ color: "var(--ink-3)" }} />
          <span className="rolepick-lbl">Viewing as</span>
          <select
            className="dl-roleselect hidden"
            value={String(roleId ?? "")}
            onChange={(e) => {
              // A <select> value is always a string, but RoleID is numeric and
              // the dashboard grants are matched on it with ===. Hand back the
              // ORIGINAL id, or the role would grant nothing and every tab would
              // disappear.
              const picked = roles.find((r) => String(r.id) === e.target.value);
              if (picked) setRoleId(picked.id);
            }}
          >
            {/* Display only — each option's VALUE is still the role id. */}
            {roles.map((r) => (
              <option key={String(r.id)} value={String(r.id)}>
                {toDisplayLabel(r.name)}
              </option>
            ))}
          </select>
        </div>
      )}
      {onExport && (
        <button className="dl-btn" onClick={onExport}>
          <Download size={15} />
          <span className="btn-lbl">Export</span>
        </button>
      )}
      <div className="dl-vr" />
      <button className="dl-logout" onClick={handleLogout}>
        <LogOut size={16} />
        <span className="logout-lbl">Logout</span>
      </button>
      {name && (
        <div className="dl-avatar" title={avatarTitle}>
          {initials}
        </div>
      )}
    </div>
  );
}
